/**
 * Dialog "Richiedi sopralluogo secondario" — wrapper sottile su SiteVisitCalendar.
 *
 * Il componente shared gestisce previous-op-ask + calendar. Questo wrapper
 * gestisce solo submit → POST /practices/{pid}/insert-site-visit-step + UI success.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, CheckCircle } from "@phosphor-icons/react";
import SiteVisitCalendar, { slotToIso, fmtSlot } from "@/components/SiteVisitCalendar";
import { SiteVisitModePicker } from "@/components/SiteVisitModePicker";

export default function RequestSiteVisitDialog({ open, onClose, practice, onDone }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pickedOperatorId, setPickedOperatorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("picking"); // picking | success
  // Durata dello step di sopralluogo iniettato. Default 30' (allineato al
  // comportamento storico), ma il PRO può alzarla a 60' o 90' per immobili
  // complessi. Il valore viene passato sia al backend (`insert-site-visit-step`)
  // sia al calendario shared (`durationOverride`) per allineare gli slot.
  const [duration, setDuration] = useState(30);
  const [mode, setMode] = useState(null); // pro | client | manual
  const [linkResult, setLinkResult] = useState(null);
  const isLinkMode = mode === "pro" || mode === "client";

  useEffect(() => {
    if (!open) return;
    setSelectedSlot(null);
    setPickedOperatorId(null);
    setResult(null);
    setError("");
    setPhase("picking");
    setDuration(30);
    setMode(null);
    setLinkResult(null);
  }, [open]);

  const sendLink = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post(`/practices/${practice._id}/secondary-visit-link`, {
        send_to: mode, visit_duration_minutes: duration,
      });
      setLinkResult(r.data);
      setPhase("link_sent");
      if (r.data?.email_sent === false) toast.warning("Link creato, ma l'email non è partita");
      else toast.success(`Link inviato a ${r.data?.email_to}`);
      if (onDone) onDone();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore invio link";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setError("");
    try {
      const body = {
        datetime: slotToIso(selectedSlot),
        visit_duration_minutes: duration,
      };
      if (pickedOperatorId) body.operator_id = pickedOperatorId;
      const r = await api.post(`/practices/${practice._id}/insert-site-visit-step`, body);
      setResult(r.data);
      setPhase("success");
      toast.success("Sopralluogo aggiunto correttamente");
      if (onDone) onDone();
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore inserimento sopralluogo";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="request-site-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={18} weight="bold" className="text-blue-600" />
            Richiedi sopralluogo
          </DialogTitle>
          <DialogDescription>
            {phase === "success"
              ? "Sopralluogo confermato"
              : phase === "link_sent"
                ? "Link di prenotazione inviato"
                : "Scegli come fissare data e ora del sopralluogo"}
          </DialogDescription>
        </DialogHeader>

        {phase === "picking" && (
          <SiteVisitModePicker value={mode} onChange={setMode} clientEmail={practice?.client_email} />
        )}

        {phase === "picking" && mode ? (
          <>
            {/* Banner informativo: chiarisce che l'operazione inietta DUE step
                (Sopralluogo + Lavorazione e consegna) e che la pratica tornerà
                al richiedente per la fase finale di consegna documenti. */}
            {mode === "manual" && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-slate-700 space-y-2" data-testid="request-visit-info-banner">
              <div className="font-semibold text-blue-900 flex items-center gap-1.5">
                <MapPin size={13} weight="fill" />
                Cosa succede al click di "Conferma sopralluogo"
              </div>
              <ol className="list-decimal list-inside space-y-1 ml-1">
                <li>
                  <b>Sopralluogo secondario</b> (durata {duration} min) — assegnato al site operator scelto qui sotto.
                </li>
                <li>
                  <b>Lavorazione e consegna</b> — al termine del sopralluogo, la pratica
                  torna automaticamente <b>a te</b> (o al Desk/Tech che gestisce la pratica)
                  per la fase finale di consegna documenti al cliente.
                </li>
              </ol>
            </div>
            )}

            {/* Selettore durata: 30/60/90 min. Segmented buttons per coerenza
                con il pattern "Sì/No, mostra tutti" usato in SiteVisitCalendar. */}
            <div className="space-y-2" data-testid="duration-picker">
              <div className="text-sm font-medium text-slate-800">
                Durata sopralluogo
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[30, 60, 90].map((mins) => {
                  const isActive = duration === mins;
                  return (
                    <button
                      key={mins}
                      type="button"
                      onClick={() => setDuration(mins)}
                      className={`text-sm py-2 rounded-md border transition-colors font-medium ${
                        isActive
                          ? "border-blue-500 bg-blue-500 text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50"
                      }`}
                      data-testid={`duration-option-${mins}`}
                    >
                      {mins} min
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-slate-500">
                Scegli una durata maggiore per immobili complessi (es. ville, edifici commerciali).
              </div>
            </div>

            {mode === "manual" && (
            <SiteVisitCalendar
              practice={practice}
              testidPrefix="req"
              durationOverride={duration}
              onSlotChange={(slot, opId) => {
                setSelectedSlot(slot);
                setPickedOperatorId(opId);
              }}
            />
            )}
          </>
        ) : phase === "link_sent" ? (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="visit-link-sent">
            <CheckCircle size={32} weight="fill" className="text-emerald-600 shrink-0" />
            <div className="text-sm text-emerald-900">
              Link inviato a <b>{linkResult?.email_to}</b> ({linkResult?.recipient_type === "client" ? "cliente finale" : "professionista"}).
              <div className="text-xs text-emerald-800 mt-0.5">
                Appena verrà scelto lo slot, il sopralluogo sarà aggiunto alla pratica e il site operator riceverà la notifica.
              </div>
            </div>
          </div>
        ) : phase === "success" ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <CheckCircle size={32} weight="fill" className="text-emerald-600 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-900" data-testid="reschedule-success-title">
                  Sopralluogo confermato con successo!
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Il site operator ha ricevuto la notifica.
                </div>
              </div>
            </div>
            <div className="rounded-md border p-3 text-sm space-y-1">
              <div><b>Operatore sopralluogo:</b> {result?.assigned_name || "—"}</div>
              {selectedSlot && <div><b>Data:</b> {fmtSlot(selectedSlot)}</div>}
              <div><b>Durata:</b> {duration} min</div>
              {/* La consegna post-sopralluogo viene sempre iniettata dal backend:
                  il messaggio è visibile anche se `return_to_operator_id` non è
                  disponibile (es. quando l'admin richiede su una pratica dove
                  il return è auto-assegnato a runtime). */}
              <div className="text-xs text-slate-700 mt-2 pt-2 border-t bg-blue-50 -mx-3 -mb-3 px-3 py-2 rounded-b">
                <b>↩ Consegna documenti post-sopralluogo:</b> è stato aggiunto uno step
                aggiuntivo "Lavorazione e consegna" che tornerà automaticamente al Desk/Tech
                che gestisce la pratica per il completamento finale.
              </div>
            </div>
          </div>
        ) : null}

        {error && phase === "picking" && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <DialogFooter>
          {phase !== "picking" ? (
            <Button onClick={onClose} className="ht-gradient border-0" data-testid="success-close">
              Chiudi
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              {isLinkMode ? (
                <Button
                  onClick={sendLink}
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-visit-link-btn"
                >
                  {submitting ? "Invio…" : `Invia link ${mode === "client" ? "al cliente" : "al professionista"}`}
                </Button>
              ) : (
              <Button
                onClick={submit}
                disabled={!selectedSlot || submitting || mode !== "manual"}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="confirm-visit-btn"
              >
                {submitting ? "Invio…" : "Conferma sopralluogo"}
              </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
